import React from "react";

import { Button } from "~/components/button";
import { useFormContext } from "~/components/form/use-form-context";
import { useFormFieldContext } from "~/components/form/use-form-field-context";
import { IconEyeFilled, IconEyeInvisibleFilled } from "~/components/icon";
import { type VariantProps, cv } from "~/tools/class-variance";
import { type IgnorePasswordManagers, ignorePasswordManagerAttributes } from "~/tools/ignore-password-managers";
import { useScoped } from "~/tools/impulse";
import { useOnInvalidFormFocus } from "~/tools/impulse-form/use-on-invalid-form-focus";
import { mergeRefs } from "~/tools/merge-refs";

import styles from "./_input.module.css";

/**
 * Check out Figma for the design of the input:
 *
 * @link https://www.figma.com/design/YIkZms7ygvgqXYnbzDFr8Y/%23-StackState-DS?node-id=4347-1200&t=pnGLA5EyfwG7fG1F-4
 */
const variants = cv(styles.input, {
	variants: {
		variant: {
			neutral: styles.neutral,
			error: styles.error,
		},
		size: {
			sm: styles.sm,
			md: styles.md,
			lg: styles.lg,
		},
	},
	defaultVariants: {
		size: "md",
		variant: "neutral",
	},
});

export interface InputProps
	extends IgnorePasswordManagers,
		VariantProps<typeof variants>,
		Omit<React.ComponentProps<"input">, "size" | "type"> {
	/**
	 * @default "text"
	 */
	type?: "text" | "password" | "email" | "number" | "url";
	loading?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, type = "text", variant, size, ...props }, ref) => {
		const formField = useFormFieldContext();
		const invalid = useScoped((scope) => formField?.state.isInvalid(scope), [formField?.state]);
		const cn = variants({ variant: invalid ? "error" : variant, size, className });

		if (type === "password") {
			return <PasswordInput ref={ref} {...props} className={cn} />;
		}

		return <RegularInput ref={ref} {...props} className={cn} type={type} />;
	},
);

if (import.meta.env.DEV) {
	Input.displayName = "Input";
}

interface CommonInputProps extends IgnorePasswordManagers, React.ComponentProps<"input"> {
	loading?: boolean;
}

const RegularInput = React.forwardRef<HTMLInputElement, CommonInputProps>(
	({ allowPasswordManager, type, loading, disabled, readOnly, onBlur, ...props }, ref) => {
		const form = useFormContext();
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus(formField?.state);
		const submitting = useScoped((scope) => formField?.state.isSubmitting(scope), [formField?.state]);

		return (
			<input
				aria-busy={submitting || loading}
				aria-disabled={disabled}
				form={form?.id}
				id={formField?.id}
				onBlur={(event) => {
					formField?.state.setTouched(true);
					onBlur?.(event);
				}}
				readOnly={submitting || loading || disabled || readOnly}
				ref={mergeRefs([ref, focusRef])}
				type={type}
				{...ignorePasswordManagerAttributes(allowPasswordManager)}
				{...props}
			/>
		);
	},
);

const PasswordInput = React.forwardRef<HTMLInputElement, CommonInputProps>(({ className, ...props }, ref) => {
	const [isVisible, setIsVisible] = React.useState(false);

	return (
		<label className={className}>
			<RegularInput ref={ref} {...props} type={isVisible ? "text" : "password"} />

			<Button className={styles.eyeButton} icon onClick={() => setIsVisible(!isVisible)} size="sm" variant="neutral">
				{isVisible ? <IconEyeInvisibleFilled /> : <IconEyeFilled />}
			</Button>
		</label>
	);
});
